import React, { ReactNode } from 'react'
import { Spinner } from '../Progress'
import { ButtonIconPosition, ButtonTypes, ButtonVariants, TextPosition } from './types/buttonTypes'
import { LinkAsButton } from '../Link/LinkAsButton'
import { getDefaultClasses, getIconClasses, getSpinnerClasses } from './helpers/buttonClassHelpers'
import { TranslatedText } from '../../../types/TranslatedText'

interface WithScreenReaderLabel {
    children: undefined
    screenReaderLabel: Exclude<TranslatedText, null | undefined>
}

interface WithoutScreenReaderLabel {
    /** This should be required but for now it is optional due to the huge amount of rework it requires to make required */
    children?: ReactNode
    screenReaderLabel?: TranslatedText
}

export interface BaseButtonProps extends React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement> {
    variant?: ButtonVariants
    type?: ButtonTypes
    icon?: ReactNode
    textPosition?: TextPosition
    iconPosition?: ButtonIconPosition
    labelIsForScreenReadersOnly?: boolean
    disabled?: boolean
    loading?: boolean
    form?: string
    /** @deprecated Use LinkAsButton component instead */
    href?: string
    /** Allows the button to take full height of parent container. Results in no vertical padding and height of 100% */
    contained?: boolean
    onClick?: (e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void
    /** @deprecated Use LinkAsButton component instead */
    download?: string | ReactNode
}

export type ButtonProps = (BaseButtonProps & WithScreenReaderLabel) | (BaseButtonProps & WithoutScreenReaderLabel)

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
    (
        {
            variant = ButtonVariants.SECONDARY,
            type = ButtonTypes.BUTTON,
            children,
            icon,
            textPosition = TextPosition.CENTER,
            iconPosition = ButtonIconPosition.LEFT,
            labelIsForScreenReadersOnly = false,
            screenReaderLabel,
            disabled = false,
            loading = false,
            href,
            form,
            contained = false,
            download,
            ...props
        },
        ref,
    ) => {
        if (href)
            return (
                <LinkAsButton
                    href={href}
                    download={download}
                    variant={variant}
                    icon={icon}
                    textPosition={textPosition}
                    iconPosition={iconPosition}
                    labelIsForScreenReadersOnly={labelIsForScreenReadersOnly}
                    screenReaderLabel={screenReaderLabel}
                    contained={contained}
                    {...props}
                >
                    {children}
                </LinkAsButton>
            )

        const classes = getDefaultClasses({ textPosition, variant, contained, disabled, className: props.className })
        const iconClasses = getIconClasses({ iconPosition, hasChildren: !!children, labelIsForScreenReadersOnly })
        const spinnerClasses = getSpinnerClasses(variant)

        return (
            <button
                type={type}
                disabled={disabled}
                aria-disabled={disabled}
                form={form}
                {...(props as React.HTMLAttributes<HTMLButtonElement>)}
                ref={ref as React.Ref<HTMLButtonElement>}
                className={classes}
            >
                <span className={`inline-flex items-center ${loading ? 'invisible' : ''}`}>
                    {icon && <span className={iconClasses}>{icon}</span>}

                    {screenReaderLabel && <span className="sr-only">{screenReaderLabel}</span>}
                    {children && children}
                </span>

                {loading && <Spinner className={spinnerClasses} />}
            </button>
        )
    },
)
export { ButtonVariants, ButtonIconPosition }
